// src/components/atoms/Select/index.tsx
import React from 'react';
import {
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  FormHelperText,
  SelectProps as MuiSelectProps,
  ListSubheader,
  SelectChangeEvent,
} from '@mui/material';
import styles from '@/components/atoms/Select/select.module.css';

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

export type SelectData = SelectOption[] | SelectGroup[];

interface SelectProps {
  // Required props
  name: string;
  value: string | string[];
  onChange: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void;
  options: SelectData;

  // Optional props
  id?: string;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined' | 'filled';
  className?: string;
  defaultOpen?: boolean;
  autoWidth?: boolean;
  showPlaceholder?: boolean;

  // Style overrides
  MenuProps?: MuiSelectProps['MenuProps'];
  sx?: MuiSelectProps['sx'];
}

const isGroupedOptions = (options: SelectData): options is SelectGroup[] => {
  return options.length > 0 && 'options' in options[0];
};

const AtomSelect: React.FC<SelectProps> = ({
  // Required props
  name,
  value,
  onChange,
  options,

  // Optional props with defaults
  id = `select-${name}`,
  label,
  placeholder,
  error = false,
  helperText,
  required = false,
  disabled = false,
  multiple = false,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  className = '',
  defaultOpen = false,
  autoWidth = false,
  showPlaceholder = false,

  // Style override props
  MenuProps,
  sx,
}) => {
  const labelId = `${id}-label`;
  const isGrouped = isGroupedOptions(options);

  const renderSelectItems = () => {
    if (isGrouped) {
      return (options as SelectGroup[]).map(group => [
        <ListSubheader key={group.label} className={styles.groupHeader}>
          {group.label}
        </ListSubheader>,
        ...group.options.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className={styles.menuItem}
          >
            {option.label}
          </MenuItem>
        )),
      ]);
    }

    return (options as SelectOption[]).map(option => (
      <MenuItem
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        className={styles.menuItem}
      >
        {option.label}
      </MenuItem>
    ));
  };

  return (
    <FormControl
      className={`${styles.formControl} ${className}`}
      error={error}
      disabled={disabled}
      required={required}
      fullWidth={fullWidth}
      size={size}
      variant={variant}
    >
      {label && (
        <InputLabel id={labelId} className={styles.label}>
          {label}
        </InputLabel>
      )}

      <MuiSelect
        id={id}
        labelId={labelId}
        name={name}
        value={value}
        onChange={onChange}
        label={label}
        multiple={multiple}
        defaultOpen={defaultOpen}
        autoWidth={autoWidth}
        className={`${styles.select} ${size === 'small' ? styles.small : ''}`} /* CHANGED: Added small class */
        displayEmpty={showPlaceholder}
        MenuProps={{
          classes: { paper: styles.menuPaper },
          ...MenuProps,
        }}
        sx={sx}
      >
        {showPlaceholder && !multiple && (
          <MenuItem value="" className={styles.placeholder}>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {renderSelectItems()}
      </MuiSelect>

      {helperText && <FormHelperText className={styles.helperText}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default AtomSelect;
